import { useState, useRef, useEffect } from 'react';
import { WorkScheduleSlider } from './WorkScheduleSlider';
import { ShowMoreButton, ShowLessButton } from './ShowMoreControls';
import type { JobPosting } from '@applicant-reviewer/api';
import CompanyLogo from '../../../../components/ui/CompanyLogo';

interface JobLandingPageProps {
  job: JobPosting;
  
  // Actions
  onNext: (weeklyPay: number, daysPerWeek: number, hoursPerDay: number) => void;
  onWatchVideo?: () => void;
}

export default function JobLandingPage({
  job,
  onNext,
  onWatchVideo,
}: JobLandingPageProps) {
  // Calculator state
  const [daysPerWeek, setDaysPerWeek] = useState(job.scheduleConfiguration?.daysSlider.defaultValue ?? 5);
  const [hoursPerDay, setHoursPerDay] = useState(job.scheduleConfiguration?.hoursSlider.defaultValue ?? 8);
  
  // Use the minimum pay rate for conservative estimates
  const hourlyRate = job.compensation?.min ?? 0;
  const weeklyPay = daysPerWeek * hoursPerDay * hourlyRate;

  // Description truncation state
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  
  // New: Ref for the container to measure available space
  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // Check for text overflow on mount/resize
  useEffect(() => {
    const checkOverflow = () => {
      if (descriptionRef.current && descriptionContainerRef.current) {
        // Compare text height to container height
        setIsOverflowing(descriptionRef.current.scrollHeight > descriptionContainerRef.current.clientHeight);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [job.description, isExpanded]); // Re-check when expansion state changes

  return (
    <div className="flex flex-col bg-white h-[100dvh]">
      {/* 
         Top Section: Changed logic 
         - If Expanded: overflow-y-auto (scrolls normally)
         - If Collapsed: flex-1 flex-col overflow-hidden (fills space, no scroll, clips content)
      */}
      <div className={`flex flex-col ${isExpanded ? 'overflow-y-auto z-20 bg-white' : 'flex-1 overflow-hidden'}`}>
        
        {/* Company Info - flex-shrink-0 ensures it doesn't get squished */}
        <div className="flex-shrink-0 px-5 pt-[16px] pb-[14px]">
          <div className="flex items-start gap-2">
            {/* Company Logo */}
            <CompanyLogo 
              src={job.companyLogo ?? ""}
              alt={job.companyName}
              className="w-[57px] h-[57px] rounded-lg"
            />
            
            {/* Company Info */}
            <div className="flex-1">
              <h2 className="text-[19px] font-bold text-[#28292d] leading-[1.4]">
                {job.companyName}
              </h2>
              <p className="text-[17px] text-[#28292d] leading-[1.4]">
                {job.locationName ?? ""}
              </p>
            </div>
            

          </div>
        </div>

        {/* Divider */}
        <div className="flex-shrink-0 border-t border-[#b9babf] mx-5" />

        {/* 
           Job Title & Description Wrapper 
           - If Collapsed: flex-1 min-h-0 (takes all remaining space in top section)
        */}
        <div className={`px-5 pt-[23px] pb-6 flex flex-col ${isExpanded ? '' : 'flex-1 min-h-0'}`}>
          <div className="flex-shrink-0 flex items-center gap-2 mb-3">
            <h1 className="text-[23px] font-bold text-[#28292d] leading-[1.4]">
              {job.title}
            </h1>
            <div 
              className="h-[30px] px-[14px] rounded-[16px] flex items-center justify-center"
              style={{ backgroundColor: '#eeeeee' }}
            >
              <span className="text-[19px] text-black font-normal leading-[1.4]">
                ${job.compensation?.min ?? 0} - ${job.compensation?.max ?? 0}/hr
              </span>
            </div>
          </div>
          
          {/* 
             Description Container 
             - If Collapsed: flex-1 min-h-0 overflow-hidden (fills remaining space, clips text)
             - If Expanded: auto height
          */}
          <div 
            ref={descriptionContainerRef}
            className={`relative ${isExpanded ? '' : 'flex-1 min-h-0 overflow-hidden'}`}
          >
            <p 
              ref={descriptionRef}
              // Removed line-clamp-6. Just standard text now.
              className="text-[19px] text-[#28292d] leading-[1.4] transition-all duration-300"
            >
              {job.description}
            </p>
            
            {/* Show More Overlay */}
            {!isExpanded && isOverflowing && (
              <div className="absolute bottom-0 left-0 right-0 h-[40px] bg-gradient-to-t from-white via-white/90 to-transparent flex items-end justify-center">
                <div className="bg-white">
                  <ShowMoreButton onClick={() => setIsExpanded(true)} />
                </div>
              </div>
            )}

            {/* Show Less Control */}
            {isExpanded && (
              <div className="flex justify-center mt-2">
                <ShowLessButton onClick={() => setIsExpanded(false)} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Section: Calculator & Actions - Hide/Push down when Expanded */}
      <div className={`flex-shrink-0 px-5 pb-6 pt-2 bg-white z-10 transition-transform duration-300 ${isExpanded ? 'translate-y-full absolute bottom-0 w-full' : ''}`}>
        <h2 className="text-[23px] font-bold text-[#28292d] leading-[1.4] mb-[6px]">
          I want to Work:
        </h2>

        {/* Days Per Week Slider */}
        <div className="mb-0">
          <WorkScheduleSlider 
            label="Days per Week"
            value={daysPerWeek}
            onChange={setDaysPerWeek}
            max={job.scheduleConfiguration?.daysSlider.max ?? 7}
            unit="day"
          />
        </div>

        {/* Hours Per Day Slider */}
        <div className="mt-0">
          <WorkScheduleSlider 
            label="Hours per Day"
            value={hoursPerDay}
            onChange={setHoursPerDay}
            max={job.scheduleConfiguration?.hoursSlider.max ?? 12}
            unit="hour"
          />
        </div>

        {/* Estimated Weekly Pay - Bordered Card */}
        <div className="mt-[16px] mb-[16px] border border-[#b9babf] rounded-[20px] py-4 text-center mx-1">
          <p className="text-[19px] text-[#8e8e8f] leading-[1.4] mb-1">
            Estimated Weekly Pay
          </p>
          <p className="text-[47px] font-bold text-[#12b71a] leading-[1.4] mb-1">
            ${weeklyPay}
          </p>
          <p className="text-[17px] text-[#8e8e8f] leading-[1.4]">
            {daysPerWeek} days × {hoursPerDay} hours × ${hourlyRate}/hr
          </p>
        </div>

        {/* Footer Button */}
        <button
          onClick={() => onNext(weeklyPay, daysPerWeek, hoursPerDay)}
          className="w-full h-[40px] bg-[#0156FF] text-white text-[18px] font-bold rounded-full shadow-lg active:scale-[0.98] transition-transform"
        >
          Set Schedule
        </button>
      </div>

      {/* Home Indicator (iOS style) */}
      <div className="fixed bottom-1 left-1/2 transform -translate-x-1/2 w-[134px] h-[5px] bg-[#28292d] rounded-[10px] z-50 pointer-events-none" />
    </div>
  );
}


